import type { Book, CompletionDate, ReadingProgress } from '../domain/entities/types';
import { BIBLE_TOTAL_CHAPTERS } from '../domain/bibleCanon';

export const createId = (): string => crypto.randomUUID();
export const nowInstant = (): string => new Date().toISOString();
export const nextInstantAfter = (previous: string): string => {
  const now = Date.now();
  const previousMs = Date.parse(previous);
  return new Date(Math.max(now, Number.isFinite(previousMs) ? previousMs + 1 : now)).toISOString();
};

export const toLocalDate = (date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface ClockTimeParts {
  hours: string;
  minutes: string;
}

export const emptyClockTime = (): ClockTimeParts => ({ hours: '', minutes: '' });

export const clockTimeFromInstant = (instant: string): ClockTimeParts => {
  const date = new Date(instant);
  return {
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0')
  };
};

export const manualReadingInterval = (
  occurredOn: string,
  start: ClockTimeParts,
  finish: ClockTimeParts
): { startedAt: string; finishedAt: string; durationMs: number } | undefined => {
  const startTouched = Boolean(start.hours || start.minutes);
  const finishTouched = Boolean(finish.hours || finish.minutes);
  if (!startTouched && !finishTouched) return undefined;
  const complete = (parts: ClockTimeParts): boolean => /^\d{1,2}$/.test(parts.hours)
    && /^\d{1,2}$/.test(parts.minutes)
    && Number(parts.hours) <= 23
    && Number(parts.minutes) <= 59;
  if (!complete(start) || !complete(finish)) throw new Error('Informe os horários inicial e final completos.');
  const localInstant = (parts: ClockTimeParts): Date => new Date(
    Number(occurredOn.slice(0, 4)),
    Number(occurredOn.slice(5, 7)) - 1,
    Number(occurredOn.slice(8, 10)),
    Number(parts.hours),
    Number(parts.minutes),
    0,
    0
  );
  const started = localInstant(start);
  const finished = localInstant(finish);
  const matchesClock = (date: Date, parts: ClockTimeParts): boolean => date.getHours() === Number(parts.hours) && date.getMinutes() === Number(parts.minutes);
  if (Number.isNaN(started.getTime()) || Number.isNaN(finished.getTime()) || toLocalDate(started) !== occurredOn || toLocalDate(finished) !== occurredOn || !matchesClock(started, start) || !matchesClock(finished, finish)) {
    throw new Error('Informe uma data e horários válidos.');
  }
  if (finished.getTime() <= started.getTime()) throw new Error('O horário final precisa ser posterior ao horário inicial.');
  return { startedAt: started.toISOString(), finishedAt: finished.toISOString(), durationMs: finished.getTime() - started.getTime() };
};

export const completionYear = (completion?: CompletionDate): number | undefined => {
  if (!completion || completion.precision === 'unknown') return undefined;
  return completion.precision === 'day' ? Number(completion.date.slice(0, 4)) : completion.year;
};

export const completionMonth = (completion?: CompletionDate): { year: number; month: number } | undefined => {
  if (!completion || completion.precision === 'unknown' || completion.precision === 'year') return undefined;
  return completion.precision === 'day'
    ? { year: Number(completion.date.slice(0, 4)), month: Number(completion.date.slice(5, 7)) }
    : { year: completion.year, month: completion.month };
};

export const formatCompletionDate = (completion: CompletionDate): string => {
  if (completion.precision === 'unknown') return 'Data da leitura não informada';
  if (completion.precision === 'year') return `Lido em ${completion.year}`;
  if (completion.precision === 'month') {
    const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
      .format(new Date(Date.UTC(completion.year, completion.month - 1, 1)));
    return `Lido em ${label}`;
  }
  const label = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${completion.date}T12:00:00Z`));
  return `Concluído em ${label}`;
};

export const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('pt-BR');

export const progressValue = (progress: ReadingProgress): number => {
  if (progress.kind === 'pages') return progress.currentPage;
  if (progress.kind === 'audio_time') return progress.currentMinutes;
  if (progress.kind === 'bible_chapters') return progress.completedChapterIds.length;
  return progress.currentPercent;
};

export const progressTotal = (progress: ReadingProgress): number | undefined => {
  if (progress.kind === 'pages') return progress.totalPages;
  if (progress.kind === 'audio_time') return progress.totalMinutes;
  if (progress.kind === 'bible_chapters') return BIBLE_TOTAL_CHAPTERS;
  return 100;
};

export const progressPercentage = (book: Book): number | undefined => {
  const total = progressTotal(book.progress);
  if (!total) return undefined;
  return Math.min(100, Math.max(0, (progressValue(book.progress) / total) * 100));
};

export const formatDuration = (milliseconds: number): string => {
  const totalMinutes = Math.max(0, Math.round(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}min`;
  return `${hours}h ${String(minutes).padStart(2, '0')}min`;
};

export const formatTimer = (milliseconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

export const statusLabel: Record<Book['status'], string> = {
  want_to_read: 'Quero ler',
  reading: 'Lendo',
  finished: 'Lido',
  paused: 'Pausado',
  abandoned: 'Abandonado'
};

export const formatLabel: Record<Book['format'], string> = {
  physical: 'Livro físico',
  ebook: 'Ebook',
  audiobook: 'Audiobook'
};

export const getProgressLabel = (progress: ReadingProgress): string => {
  if (progress.kind === 'pages') {
    return progress.totalPages
      ? `${progress.currentPage} de ${progress.totalPages} páginas`
      : `Página ${progress.currentPage}`;
  }
  if (progress.kind === 'audio_time') {
    return `${progress.currentMinutes} de ${progress.totalMinutes} min`;
  }
  if (progress.kind === 'bible_chapters') {
    return `${progress.completedChapterIds.length.toLocaleString('pt-BR')} de ${BIBLE_TOTAL_CHAPTERS.toLocaleString('pt-BR')} capítulos`;
  }
  return `${Math.round(progress.currentPercent)}%`;
};
